import { useEffect, useState } from "react";
import { canExposePilotSurface } from "@enrein/shared-contracts/pilot-exposure";
import type { ClientChangeRequestTimelineV1 } from "@enrein/shared-contracts";
import type { PreviewViewV1 } from "@enrein/shared-contracts/approval-change";
import type { V10ClientLinkViewProps } from "../types.js";
import { V10EvidenceLegacyScope } from "../components/V10EvidenceLegacyScope.js";
import { V10SafeEditSection } from "../components/V10SafeEditSection.js";
import { deriveClientChangeRequestPresentation } from "../adapters/v10-change-request-presentation.js";

export function V10ChangesTab({
  changeMessage,
  setChangeMessage,
  active,
  canAct,
  onRequestChange,
  shellStatus,
  api,
  linkId,
  token,
  deployEnv,
  onActionStatus,
  preview,
  presentationScopeId,
  onOpenRevisedPreview,
}: Pick<V10ClientLinkViewProps, "changeMessage" | "setChangeMessage" | "api" | "deployEnv" | "onActionStatus"> & {
  readonly active: boolean;
  readonly canAct: boolean;
  readonly onRequestChange: () => void;
  readonly shellStatus: string;
  readonly linkId: string;
  readonly token: string;
  readonly preview: PreviewViewV1 | null;
  readonly presentationScopeId: string;
  readonly onOpenRevisedPreview: (previewVersionId: string) => void;
}) {
  const canSend = canAct && changeMessage.trim().length > 0;
  const [timeline, setTimeline] = useState<ClientChangeRequestTimelineV1 | null>(null);
  const showSafeEdit =
    shellStatus === "SAFE_EDIT_ACTIVE" &&
    canExposePilotSurface("client.safePatch", "client", {
      deployEnv,
      nodeEnv: import.meta.env.MODE,
      explicitFlags: import.meta.env.VITE_ENREIN_PILOT_FEATURE_FLAGS,
    }).enabled;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void api.listChangeRequests(linkId, token).then((next) => {
      if (!cancelled) setTimeline(next);
    });
    return () => {
      cancelled = true;
    };
  }, [active, api, linkId, token, changeMessage]);

  return (
    <section
      className={`v10-cl-tab-panel${active ? " active" : ""}`}
      role="tabpanel"
      id="v10-tab-changes"
      aria-labelledby="v10-tab-btn-changes"
      hidden={!active}
    >
      {timeline && timeline.entries.length > 0 ? (
        <article className="v10-cl-card v10-cl-change-timeline" data-testid="change-request-timeline">
          <h3>Your change requests</h3>
          <ul>
            {timeline.entries.map((entry, index) => {
              const presentation = deriveClientChangeRequestPresentation(
                entry,
                presentationScopeId,
                preview?.previewVersionId,
              );
              const openPreviewAction =
                presentation?.primaryAction?.intent === "OPEN_CLIENT_REVISED_PREVIEW"
                  ? presentation.primaryAction
                  : null;
              const versionRef =
                presentation?.relevantRecords.find((record) => record.startsWith("Revised preview ")) ??
                null;
              return (
                <li key={`${entry.createdAt}-${index}`}>
                  <p>
                    <strong>{presentation?.title ?? entry.statusLabel}</strong>
                    {presentation?.reason ? ` — ${presentation.reason}` : ` — ${entry.resolutionLabel}`}
                  </p>
                  <p className="v10-cl-muted">{entry.message}</p>
                  {versionRef ? (
                    <p className="v10-cl-muted" data-testid="resolved-preview-version">
                      {versionRef}
                    </p>
                  ) : null}
                  {presentation?.condition === "NEEDS_ATTENTION" ? (
                    <p className="v10-cl-muted">Revised preview confirmation is incomplete</p>
                  ) : null}
                  {openPreviewAction ? (
                    <div className="v10-cl-actions">
                      <button
                        type="button"
                        className="v10-cl-btn v10-cl-btn--primary"
                        data-testid="open-revised-preview"
                        data-preview-version-id={openPreviewAction.previewVersionId}
                        onClick={() => onOpenRevisedPreview(openPreviewAction.previewVersionId)}
                      >
                        {openPreviewAction.label}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </article>
      ) : null}
      <label htmlFor="v10-change">Change request</label>
      {!canAct ? (
        <p className="v10-cl-muted">Change requests are closed for the current decision state.</p>
      ) : null}
      <textarea
        id="v10-change"
        value={changeMessage}
        onChange={(event) => setChangeMessage(event.target.value)}
        placeholder="Describe the change you want the agency to review."
        disabled={!canAct}
      />
      <div className="v10-cl-actions">
        <button
          type="button"
          className="v10-cl-btn v10-cl-btn--primary"
          disabled={!canSend}
          onClick={onRequestChange}
        >
          Send change request
        </button>
      </div>
      {showSafeEdit ? (
        <V10EvidenceLegacyScope>
          <V10SafeEditSection api={api} linkId={linkId} token={token} onActionStatus={onActionStatus} />
        </V10EvidenceLegacyScope>
      ) : null}
    </section>
  );
}
